/**
 * Donde se ven los textos que el dueno del producto edita en su consola.
 *
 * El defecto que este archivo cierra
 * ----------------------------------
 * La consola del dueno guardaba cinco textos, decia «Lo que ven los usuarios de
 * todas las organizaciones» y cada campo declaraba en que parte de la pantalla
 * aparecia. No aparecian en ninguna: no habia un solo consumidor. La mitad
 * literal de lo que el dueno pidio —«poder modificar la pagina»— no existia.
 *
 * Envuelve la app en vez de meterse adentro
 * -----------------------------------------
 * Se monta en `main.tsx` alrededor de `<App/>` y no dentro de las pantallas.
 * Dos razones, y la segunda es la que manda:
 *
 * 1. Estos textos valen para TODO: la pantalla de ingreso, el ERP y la
 *    pantalla de error de conexion. Repartirlos por las pantallas
 *    garantizaria que alguna se quede sin el aviso de mantenimiento, que es
 *    justo el que no se puede perder.
 * 2. Asi el aviso se dibuja **aunque la app de adentro se caiga**: esta por
 *    fuera de todo `ErrorBoundary`. Si el sistema esta roto es cuando mas hace
 *    falta que se lea «estamos trabajando en esto».
 *
 * Por que no hay overlays ni posiciones fijas
 * -------------------------------------------
 * La franja de aviso y el pie van en el flujo normal del documento: la primera
 * empuja el contenido hacia abajo, el segundo queda al final. Un elemento fijo
 * habria quedado siempre a la vista, si, y tambien habria tapado el ultimo
 * renglon de cada pantalla del ERP para siempre. Un aviso que se lee tapando la
 * fila que la persona esta cargando no es un aviso: es un estorbo permanente.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { leerMarca, TEXTOS_DE_FABRICA, type Marca } from '@/marca/api';

/**
 * Trae los textos una vez por carga de pagina.
 *
 * No usa React Query a proposito: esto tiene que funcionar igual en el arbol que
 * envuelve al proveedor y en la pantalla de ingreso, y no gana nada con cache
 * compartida —se pide una sola vez— ni con reintentos, que `leerMarca` ya
 * resuelve devolviendo los valores de fabrica.
 */
function useMarca(): Marca {
  const [marca, setMarca] = useState<Marca>(TEXTOS_DE_FABRICA);

  useEffect(() => {
    let vigente = true;
    void leerMarca().then((valores) => {
      if (vigente) setMarca(valores);
    });
    return () => {
      vigente = false;
    };
  }, []);

  return marca;
}

/**
 * Pone el nombre comercial en el titulo de la pestana.
 *
 * Es la unica superficie de la marca que no se dibuja con React: el titulo lo
 * lee el navegador del `<head>`, y `index.html` lo trae en duro. Sin esto, el
 * dueno cambia el nombre del producto y la pestana sigue diciendo el anterior en
 * todas las organizaciones.
 */
function useTituloDeLaPestana(nombre: string): void {
  useEffect(() => {
    const limpio = nombre.trim();
    if (limpio !== '') document.title = limpio;
  }, [nombre]);
}

/**
 * La franja de mantenimiento, arriba de todo.
 *
 * `role="status"` y no `role="alert"`: un lector de pantalla no tiene que
 * interrumpir a la persona a mitad de una frase para leerle que el sabado hay
 * mantenimiento. Se anuncia cuando termine lo que esta diciendo.
 *
 * `whitespace-pre-line` respeta los saltos de linea del texto y nada mas: el
 * campo admite varias lineas (es el unico) y el backend ya recorto los espacios
 * de los bordes de cada una.
 */
function FranjaDeAviso({ texto }: { texto: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-marca="aviso"
      className="whitespace-pre-line border-b border-warning/30 bg-warning-soft px-4 py-2 text-center text-sm text-fg"
    >
      {texto}
    </div>
  );
}

/**
 * El pie: nombre del producto, texto libre y contacto de soporte.
 *
 * Se dibuja solo si hay algo que decir. Un pie vacio en todas las pantallas del
 * sistema, en todas las organizaciones, para un despliegue que nunca abrio esta
 * consola, es ruido puro: la ausencia de configuracion tiene que verse igual que
 * antes de que esto existiera.
 *
 * `leyenda_ingreso` aparece aca **solo sin sesion**, que es donde el campo dice
 * que aparece: es la bajada de la pantalla de ingreso, no un texto del ERP.
 */
function PieDeLaPagina({ marca, anonimo }: { marca: Marca; anonimo: boolean }) {
  const piezas = [
    anonimo ? marca.leyenda_ingreso.trim() : '',
    marca.nombre_comercial.trim(),
    marca.pie.trim(),
    marca.contacto_soporte.trim(),
  ].filter((pieza) => pieza !== '');

  if (piezas.length === 0) return null;

  return (
    <footer
      data-marca="pie"
      className="border-t border-line bg-app px-4 py-3 text-center text-xs text-fg-faint"
    >
      {piezas.map((pieza, indice) => (
        <span key={pieza}>
          {indice > 0 && <span aria-hidden="true"> · </span>}
          {pieza}
        </span>
      ))}
    </footer>
  );
}

/**
 * Envuelve la aplicacion con los textos del dueno.
 *
 * `anonimo` lo pasa quien la monta y no se lee del store de sesion a proposito:
 * este arbol esta por FUERA del proveedor de datos y del guardian de sesion, y
 * suscribirlo al store lo ataria al orden de arranque. Ver `main.tsx`.
 */
export function MarcaShell({ children }: { children: ReactNode }) {
  const marca = useMarca();
  useTituloDeLaPestana(marca.nombre_comercial);

  const aviso = marca.aviso_mantenimiento.trim();
  const anonimo = useEsAnonimo();

  return (
    <>
      {aviso !== '' && <FranjaDeAviso texto={aviso} />}
      {children}
      <PieDeLaPagina marca={marca} anonimo={anonimo} />
    </>
  );
}

/**
 * `true` mientras no haya credenciales guardadas.
 *
 * Se mira `localStorage` y no el store de Zustand por lo dicho arriba: este
 * componente vive por fuera del arbol de la app. Es una senal aproximada —dice
 * «hay un token guardado», no «la sesion es valida»— y alcanza de sobra para lo
 * unico que decide: si mostrar la bajada de la pantalla de ingreso. Equivocarse
 * cuesta un renglon de texto de mas o de menos en el pie.
 */
function useEsAnonimo(): boolean {
  const [anonimo, setAnonimo] = useState(() => !hayCredenciales());

  useEffect(() => {
    const revisar = () => {
      setAnonimo(!hayCredenciales());
    };
    // `storage` avisa de los cambios hechos en OTRA pestana. Para los de esta,
    // el intervalo: es un renglon del pie, no vale un canal de eventos nuevo
    // atravesando la app.
    window.addEventListener('storage', revisar);
    const reloj = window.setInterval(revisar, 2000);
    return () => {
      window.removeEventListener('storage', revisar);
      window.clearInterval(reloj);
    };
  }, []);

  return anonimo;
}

function hayCredenciales(): boolean {
  try {
    return window.localStorage.getItem('nucleo.auth') !== null;
  } catch {
    // `localStorage` bloqueado (modo privado estricto). Se asume anonimo: es el
    // estado en el que el texto de mas es inofensivo.
    return true;
  }
}
